import React            from 'react';
import ThemeModeContext from '../contexts/ThemeModeContext';
import useTheme         from './useTheme';

// @stonecode/notifications-sidebar est un paquet framework-agnostic monté hors de l'arbre React
// (insertAdjacentHTML sur un container donné), donc ses couleurs --nc-* ne peuvent pas être
// surchargées par une simple règle CSS var(--ant-color-xxx) : ce paquet n'est pas forcément un
// descendant DOM du wrapper sur lequel antd expose ses tokens en cssVar. On lit donc les tokens
// via le Context React (useTheme, insensible à la position dans le DOM) et on les pose nous-mêmes
// sur :root, qui lui est bien un ancêtre de tout, y compris du panneau de notifications.
export default function useSyncNotificationCenterTheme() {
	const { mode } = React.useContext(ThemeModeContext);
	const {
		colorBgContainer,
		colorBgContainerDisabled,
		colorBgLayout,
		colorBorder,
		colorBorderSecondary,
		colorError,
		colorFillTertiary,
		colorFillQuaternary,
		colorPrimary,
		colorPrimaryBg,
		colorSuccess,
		colorTextBase,
		colorTextDescription,
		colorTextDisabled,
		colorTextQuaternary,
		colorWarning,
		controlItemBgHover,
	} = useTheme();

	React.useEffect(() => {
		const root = document.documentElement.style;

		root.setProperty('--nc-color-primary', colorPrimary);
		root.setProperty('--nc-text-color', colorTextBase);
		root.setProperty('--nc-color-sidebar-bg', colorBgContainer);
		root.setProperty('--nc-color-surface', colorBgLayout);
		root.setProperty('--nc-color-surface-hover', controlItemBgHover);
		// 4 teintes distinctes : 
		// --nc-color-item-bg : notification non-lue
		// --nc-color-item-bg-hover : notification non-lue hover
		// --nc-color-item-bg-read : notification lue
		// --nc-color-item-bg-read-hover : notification lue hover
		if (mode === 'dark') {
			root.setProperty('--nc-color-item-bg', colorFillTertiary);
			root.setProperty('--nc-color-item-bg-hover', colorFillQuaternary);
			root.setProperty('--nc-color-item-bg-read', colorBgContainer);
			root.setProperty('--nc-color-item-bg-read-hover', colorBgContainer);
		} else {
			root.setProperty('--nc-color-item-bg', `rgb(from ${colorPrimary} r g b / 0.15)`);
			root.setProperty('--nc-color-item-bg-hover', `rgb(from ${colorPrimary} r g b / 0.10)`);
			root.setProperty('--nc-color-item-bg-read', colorBgContainer);
			root.setProperty('--nc-color-item-bg-read-hover', colorBgContainer);
		}
		root.setProperty('--nc-color-border', colorBorder);
		root.setProperty('--nc-color-divider', colorBorderSecondary);
		root.setProperty('--nc-color-text-muted', colorTextDescription);
		root.setProperty('--nc-color-disabled-bg', colorBgContainerDisabled);
		root.setProperty('--nc-color-text-disabled', colorTextDisabled);
		root.setProperty('--nc-color-text-disabled-subtle', colorTextQuaternary);
		root.setProperty('--nc-color-level-low', colorSuccess);
		root.setProperty('--nc-color-level-medium', colorWarning);
		root.setProperty('--nc-color-level-high', colorError);
	}, [
		colorBgContainer,
		colorBgContainerDisabled,
		colorBgLayout,
		colorBorder,
		colorBorderSecondary,
		colorError,
		colorFillQuaternary,
		colorFillTertiary,
		colorPrimary,
		colorPrimaryBg,
		colorSuccess,
		colorTextBase,
		colorTextDescription,
		colorTextDisabled,
		colorTextQuaternary,
		colorWarning,
		mode,
		controlItemBgHover,
	]);
}
